import React from 'react'
import {
  composeRenderProps,
  Tab as RACTab,
  TabList as RACTabList,
  TabListProps,
  TabPanel as RACTabPanel,
  TabPanelProps,
  TabProps,
  Tabs as RACTabs,
  TabsProps
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { pressedRing } from './utils'

const tabsStyles = tv({
  base: 'flex gap-2',
  variants: {
    orientation: {
      horizontal: 'flex-col',
      vertical: 'flex-row w-[800px]'
    }
  }
})

export function Tabs(props: TabsProps) {
  return (
    <RACTabs
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        tabsStyles({ ...renderProps, className })
      )}
    />
  )
}

const tabListStyles = tv({
  base: 'flex justify-evenly border-0 border-b border-neutral-200 m-0 p-0',
  variants: {
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col items-start'
    }
  }
})

export function TabList<T extends object>(props: TabListProps<T>) {
  return (
    <RACTabList
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        tabListStyles({ ...renderProps, className })
      )}
    />
  )
}

const tabProps = tv({
  extend: pressedRing,
  base: `flex justify-center grow items-center cursor-pointer m-0 rounded-none p-3 border-0 border-none text-neutral-500 text-sm font-medium font-['Inter'] leading-tight`,
  variants: {
    isSelected: {
      false:
        'border-solid border-b-2 border-transparent hover:text-neutral-600 hover:bg-neutral-50',
      true: 'border-solid border-b-2 border-teal-500 text-teal-500 hover:bg-teal-50'
    },
    isDisabled: {
      true: 'text-neutral-300 cursor default selected:text-neutral-400 selected:bg-neutral-200'
    }
  }
})

interface ExtendedTabProps extends TabProps {
  isDisabled?: boolean
}

export function Tab(props: ExtendedTabProps) {
  return (
    <RACTab
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        tabProps({ ...renderProps, className })
      )}
    />
  )
}

const tabPanelStyles = tv({
  extend: pressedRing,
  base: 'flex-1 p-4 text-sm text-gray-900'
})

export function TabPanel(props: TabPanelProps) {
  return (
    <RACTabPanel
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        tabPanelStyles({ ...renderProps, className })
      )}
    />
  )
}
